import { z } from 'zod';

const jsonLikeSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(jsonLikeSchema), z.record(jsonLikeSchema)])
);

export const agentRunContextSchema = z.object({
  currentTabFilterId: z.number().int().positive().optional(),
  currentContentId: z.number().int().positive().optional(),
  selectedContentIds: z.array(z.number().int().positive()).optional(),
  hubId: z.number().int().positive().optional(),
  /** When set, tool execution may record automation provenance on this ProcessRun content id (hub-scoped). */
  processRunId: z.number().int().positive().optional(),
  extra: jsonLikeSchema.optional(),
}).strict();

export const agentRunRequestSchema = z
  .object({
    agentKey: z.string().trim().min(1).optional(),
    agentId: z.number().int().positive().optional(),
    message: z.string().trim().min(1),
    context: agentRunContextSchema.optional(),
  })
  .strict()
  .refine((data) => data.agentKey !== undefined || data.agentId !== undefined, {
    message: 'Either agentKey or agentId is required',
    path: ['agentKey'],
  });

export const agentActionSchema = z.object({
  type: z.literal('TOOL_CALL'),
  toolKey: z.string().trim().min(1),
  input: jsonLikeSchema,
  output: jsonLikeSchema.optional(),
}).strict();

export const agentRunResponseSchema = z.object({
  runId: z.string().trim().min(1),
  status: z.enum(['SUCCEEDED', 'FAILED', 'PARTIAL', 'PENDING_APPROVAL', 'CANCELLED']),
  agentKey: z.string().trim().min(1),
  reply: z.string(),
  actions: z.array(agentActionSchema).optional(),
  debug: z.object({
    reasoningSummary: z.string().optional(),
    toolCallsPlanned: z.number().int().nonnegative().optional(),
    nextActionIntent: z
      .enum([
        'ATTACH_EXISTING_ANCHOR',
        'ATTACH_EXISTING_EVENT',
        'CREATE_ANCHOR_AND_ATTACH',
        'CREATE_ANCHOR_EVENT_AND_ATTACH_DOCUMENT_SECTION',
        'CREATE_EVENT_AND_ATTACH',
        'SKIP',
      ])
      .optional(),
    createdIds: z
      .object({
        documentSectionId: z.number().int().positive().optional(),
        anchorId: z.number().int().positive().optional(),
        eventId: z.number().int().positive().optional(),
      })
      .optional(),
    captureActionIntent: z
      .enum(['CAPTURE_LINK_DOCUMENT_SECTION_TO_EVENT', 'CAPTURE_LINK_EVENT_TO_ANCHOR'])
      .optional(),
    attachmentPlanKind: z.literal('ATTACH_DOCUMENT_SECTION').optional(),
  }).optional(),
}).strict();

export type AgentRunRequest = z.infer<typeof agentRunRequestSchema>;
export type AgentRunAction = z.infer<typeof agentActionSchema>;
export type AgentRunResponse = z.infer<typeof agentRunResponseSchema>;

export type AgentRunInput = AgentRunRequest;
export type AgentRunResult = AgentRunResponse;
